import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMaintenanceTypes } from '../../hooks/useMaintenanceTypes';
import { fetchMaintenanceTypes } from '../../features/maintenanceTypes/maintenanceTypesSlice';
import { ACTIONS } from '../../constants/actionTables';
import TableComponent from '../../components/Table';
import ModalAddMaintenanceType from '../../components/ModalAddMaintenanceType';
import PageLoader from '../../components/PageLoader';
import { requestSearch } from '../../helpers/search';

import { Typography } from '@mui/material';

import styles from './style.module.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

function MaintenanceTypes() {
  const [data, setData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const dispatch = useDispatch();
  const { user } = useAuth();
  const { maintenanceTypes, isLoading } = useMaintenanceTypes();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(fetchMaintenanceTypes());
  }, [dispatch]);

  useEffect(() => {
    if (maintenanceTypes) {
      const prepared = maintenanceTypes.map((type) => ({
        id: type.id,
        number: type.id,
        name: type.name,
        description: type.description,
        flagForDelete: !type.maintenances || type.maintenances.length === 0,
      }));
      setData(prepared);
      setFilteredData(prepared);
    }
  }, [maintenanceTypes]);

  const titles = [t('generic.number'), t('generic.name'), t('generic.description')];

  if (user.role === 'admin') {
    titles.push(t('generic.action'));
  }

  const handleSearch = (searchValue) => {
    if (searchValue.trim() !== '') {
      setSortConfig({ key: null, direction: 'asc' });
    }
    requestSearch(searchValue, data || [], setFilteredData);
  };

  const getCount = () => filteredData?.length || 0;

  const handleSort = (columnKey) => {
    let newSortConfig = { key: null, direction: 'asc' };

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        newSortConfig = { key: columnKey, direction: 'desc' };
      } else if (sortConfig.direction === 'desc') {
        newSortConfig = { key: null, direction: 'asc' };
      }
    } else {
      newSortConfig = { key: columnKey, direction: 'asc' };
    }

    setSortConfig(newSortConfig);

    if (newSortConfig.key === null) {
      setFilteredData([...data]);
      return;
    }

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[columnKey];
      const bValue = b[columnKey];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (typeof aValue === 'string') {
        return newSortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return newSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredData(sorted);
  };

  return (
    <PageLoader
      loading={isLoading && !maintenanceTypes}
      headerTitle={t('generic.maintenanceTypes')}
      headerSubtitle={t('generic.maintenanceTypesSubtitle')}
    >
      <section className={styles.container}>
        <div className={styles.header}>
          <div>
            <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
              {t('generic.maintenanceTypes')}
            </Typography>
            <Typography className={styles.headerSubtitle}>
              {t('generic.maintenanceTypesSubtitle')}
            </Typography>
          </div>
          {user.role === 'admin' && <ModalAddMaintenanceType type={'add'} />}
        </div>

        {filteredData && (
          <TableComponent
            titles={titles}
            data={filteredData}
            action={ACTIONS.MAINTENANCETYPES}
            search={true}
            showTableHeader={true}
            tableTitle={t('generic.maintenanceTypes')}
            tableCount={`${getCount()} ${t('generic.maintenanceTypes')}`}
            searchPlaceholder={t('generic.search')}
            onSearch={handleSearch}
            onSort={handleSort}
            sortConfig={sortConfig}
            sortableColumns={['number', 'name', 'description']}
            columnKeys={['number', 'name', 'description']}
          />
        )}
      </section>
    </PageLoader>
  );
}

export default MaintenanceTypes;
